import { Box, Stack, Typography } from '@mui/material';
import { toLocaleNumber, useTranslation } from '@chr/online-shared-ui-localizations';

import { CarrierSummary } from '@/common/apis/dtos';
import { InformationDisplay } from '@/common/components/information-display/InformationDisplay';
import { formatLocation, IsoToDate } from '@/common/utilities/display-utilities';
import { TransportMode } from '@/common/constants/booking-types';

interface DestinationPortProps {
  carrierSummary: CarrierSummary;
  transportMode: TransportMode;
  port: Port;
}

export type Port = 'Origin' | 'Destination';

const DerivePortInDaysText = (port: Port, isActual: boolean, inDays: number) => {
  let inDaysLabel = '';
  const translate = useTranslation();
  if (isActual) {
    inDaysLabel = port === 'Origin' ? 'Departed' : 'Arrived';
  } else if (inDays > 0) {
    inDaysLabel = port === 'Origin' ? 'Departs in' : 'Arrives in';
  } else if (inDays < 0) {
    inDaysLabel =
      port === 'Origin' ? 'Estimated Departure' : 'Estimated Arrival';
  } else {
    inDaysLabel = port === 'Origin' ? 'Departs' : 'Arrives';
  }

  const absDays = Math.abs(inDays);
  const dayOrDays = absDays > 1 ? 'days' : 'day';
  let inDaysText = '';

  if (inDays > 0) {
    inDaysText = `${absDays} ${dayOrDays}`;
  } else if (inDays < 0) {
    inDaysText = translate('{0} {1} ago', [toLocaleNumber(absDays), dayOrDays]);
  } else {
    inDaysText = translate('Today');
  }

  return [inDaysLabel, inDaysText];
};

const DerivePortDateText = (port: Port, estimatedDate: string, actualDate?: string) => {
  if (actualDate) {
    return [port === 'Origin' ? 'Departed' : 'Arrived', IsoToDate(actualDate)];
  } else {
    return [
      port === 'Origin' ? 'Estimated Departure' : 'Estimated Arrival',
      IsoToDate(estimatedDate)
    ];
  }
};

interface PortSummaryProps {
  daysLabel: string;
  daysText: string;
  dateLabel: string;
  dateText: string;
}

const getPortProps = (carrierSummary: CarrierSummary, port: Port): PortSummaryProps => {
  if (port === 'Origin') {
    const hasDeparted = !!carrierSummary.originPortDepartureDate;
    const [daysLabel, daysText] = DerivePortInDaysText(
      'Origin',
      hasDeparted,
      (hasDeparted
        ? carrierSummary.originPortDepartedInDays
        : carrierSummary.originPortEstimatedDepartureInDays) as number
    );
    const [dateLabel, dateText] = DerivePortDateText(
      'Origin',
      carrierSummary.originPortEstimatedDepartureDate as string,
      carrierSummary.originPortDepartureDate
    );

    return { daysLabel, daysText, dateLabel, dateText };
  }

  const hasArrived = !!carrierSummary.destinationPortArrivalDate;
  const [daysLabel, daysText] = DerivePortInDaysText(
    'Destination',
    hasArrived,
    (hasArrived
      ? carrierSummary.destinationPortArrivedInDays
      : carrierSummary.destinationPortEstimatedArrivalInDays) as number
  );
  const [dateLabel, dateText] = DerivePortDateText(
    'Destination',
    carrierSummary.destinationPortEstimatedArrivalDate as string,
    carrierSummary.destinationPortArrivalDate
  );

  return { daysLabel, daysText, dateLabel, dateText };
};

const PortSummary = (props: DestinationPortProps) => {
  const { carrierSummary, transportMode, port } = props;
  const translate = useTranslation();

  const { dateLabel, dateText } = getPortProps(carrierSummary, port);

  const portOrAirport = transportMode === TransportMode.AIR ? 'Airport' : 'Port';
  const location =
    port === 'Origin'
      ? carrierSummary.originPortLocation
      : carrierSummary.destinationPortLocation;
  const header = translate(`{0} {1}`, [port, portOrAirport]);

  return (
    <Stack
      data-testid={`carrier-${port}-port-summary`}
      direction={{ md: 'row', xs: 'column' }}
      justifyContent={'space-between'}
      gap={1}
      p={2}
      width={'100%'}
    >
      <InformationDisplay
        data-testid="port-location"
        label={header}
        component={
          location ?
            <Box>
              <Typography variant={'subtitle2'}>{location.name}</Typography>
              <Typography variant={'subtitle2'}>{formatLocation(location)}</Typography>
            </Box>
            :
            <Typography variant={'subtitle2'}>{'-'}</Typography>
        }
      />

      <InformationDisplay
        label={dateLabel}
        text={dateText}
        data-testid={`${port}-departure-date`}
      />
    </Stack>
  );
};

export default PortSummary;
